/**
 * calculadora-envio-datos.js
 * v1 — 2026-09-25 — Fuente única de datos/lógica para la calculadora de envío.
 *
 * Antes esta lógica (tabla de comunas, matching de tarifas, fetch en vivo) estaba
 * DUPLICADA en snippets/calculadora-envio.liquid (carrito) y en
 * blocks/calculadora-envio-pdp.liquid (PDP). Las dos copias se fueron desalineando:
 * el carrito consultaba la tarifa real de Shopify (incluida la promo "$1" cuando está
 * activa) y la PDP usaba una tabla fija de precios sin la promo — mismo producto, misma
 * comuna, mismo momento, dos precios distintos en pantalla (bug real visto en vivo el
 * 2026-09-25 por Nicolás). Se consolida acá para que ambos widgets usen la misma fuente.
 *
 * window.CalculadoraEnvio expone:
 *   - UMBRAL_ENVIO_GRATIS, COMUNAS, COMUNAS_ELEGIBLES_1_PESO
 *   - money(valor)
 *   - obtenerSubtotal() -> Promise<number|null>  (subtotal real del carrito, /cart.js)
 *   - obtenerTarifas() -> Promise<array|null>    (tarifas reales, /cart/*_shipping_rates.json)
 *   - encontrarTarifa(tarifas, clave)            (matchea una comuna contra la lista real)
 *
 * v2 — 2026-09-25 — Se sacó el cacheo de `obtenerSubtotal`/`obtenerTarifas` (memoizaban
 * el resultado para toda la vida de la página). Eso quedaba stale apenas el carrito
 * cambiaba sin recargar la página (agregar otro producto, cambiar cantidad) — el drawer
 * seguía mostrando el subtotal/tarifas de antes del cambio. Ahora cada llamada pide datos
 * frescos; el costo es una llamada de red extra por interacción, aceptable dado el
 * volumen real del sitio.
 */
window.CalculadoraEnvio = (function () {
  var UMBRAL_ENVIO_GRATIS = 30000;

  // Comuna → cómo reconocerla en el nombre/descripción de la tarifa real de Shopify.
  // Único lugar del proyecto donde se mantiene esta tabla — si Shopify renombra una
  // tarifa, actualizar acá (afecta carrito y PDP por igual).
  var COMUNAS = {
    'vina-del-mar': { etiqueta: 'Viña del Mar', match: ['viña del mar'], excluir: ['retiro'] },
    'retiro-vina-del-mar': { etiqueta: 'Retiro en Viña del Mar', match: ['retiro'] },
    'valparaiso': { etiqueta: 'Valparaíso', match: ['valparaíso'], excluir: ['retiro'] },
    'concon-renaca': { etiqueta: 'Concón / Reñaca', match: ['concón', 'reñaca'] },
    'quilpue': { etiqueta: 'Quilpué', match: ['quilpué'] },
    'quintero': { etiqueta: 'Quintero', match: ['quintero'] },
    'villa-alemana': { etiqueta: 'Villa Alemana', match: ['villa alemana'] },
    'curauma-placilla': { etiqueta: 'Curauma / Placilla', match: ['curauma', 'placilla'] },
    'quillota': { etiqueta: 'Quillota', match: ['quillota'] },
    'puchuncavi': { etiqueta: 'Puchuncaví', match: ['puchuncaví'] },
    'maitencillo': { etiqueta: 'Maitencillo', match: ['maitencillo'] }
  };

  // Comunas donde hoy existe la tarifa promocional "Hoy despacho a $1" — confirmado en
  // vivo el 2026-09-25. Se usa como fallback cuando no hay consulta en vivo disponible
  // (ej. PDP con el carrito todavía vacío).
  var COMUNAS_ELEGIBLES_1_PESO = ['vina-del-mar', 'valparaiso', 'concon-renaca'];

  function money(valor) {
    return Math.round(Number(valor)).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
  }

  function normalizar(texto) {
    return (texto || '').toLowerCase();
  }

  function obtenerSubtotal() {
    // /cart.js representa el total en centésimos (x100) incluso en CLP — convención de
    // Shopify, distinta a la de /cart/*_shipping_rates.json que usa el monto en pesos tal cual.
    return fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (data) { return Number(data.total_price) / 100; })
      .catch(function () { return null; });
  }

  function obtenerTarifas() {
    var params = 'shipping_address%5Bcountry%5D=Chile&shipping_address%5Bprovince%5D=Valpara%C3%ADso';

    return fetch('/cart/prepare_shipping_rates.json?' + params, { method: 'POST' })
      .then(function () {
        function intentar(restantes) {
          return fetch('/cart/async_shipping_rates.json?' + params)
            .then(function (res) {
              if (res.status === 200) return res.json();
              if (restantes <= 0) throw new Error('sin respuesta');
              return new Promise(function (resolve) {
                setTimeout(resolve, 1000);
              }).then(function () {
                return intentar(restantes - 1);
              });
            });
        }
        return intentar(6);
      })
      .then(function (data) {
        var rates = (data && data.shipping_rates) || [];
        // Carrito vacío: Shopify devuelve lista vacía, no error — se trata igual que
        // "no disponible" para que cada widget decida su propio fallback.
        return rates.length ? rates : null;
      })
      .catch(function () {
        return null; // fail-silent
      });
  }

  function encontrarTarifa(tarifas, clave) {
    var config = COMUNAS[clave];
    if (!config || !tarifas) return null;

    var candidatas = tarifas.filter(function (tarifa) {
      var texto = normalizar(tarifa.name) + ' ' + normalizar(tarifa.description);
      var coincide = config.match.some(function (c) { return texto.indexOf(c) !== -1; });
      var excluido = (config.excluir || []).some(function (c) { return texto.indexOf(c) !== -1; });
      return coincide && !excluido;
    });
    if (!candidatas.length) return null;
    // Si hay varias (ej. tarifa normal + promo), la más barata es la que realmente aplica.
    candidatas.sort(function (a, b) { return Number(a.price) - Number(b.price); });
    return candidatas[0];
  }

  return {
    UMBRAL_ENVIO_GRATIS: UMBRAL_ENVIO_GRATIS,
    COMUNAS: COMUNAS,
    COMUNAS_ELEGIBLES_1_PESO: COMUNAS_ELEGIBLES_1_PESO,
    money: money,
    obtenerSubtotal: obtenerSubtotal,
    obtenerTarifas: obtenerTarifas,
    encontrarTarifa: encontrarTarifa
  };
})();
